import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Activity, FileText, TrendingUp, Users } from 'lucide-react';
import { formatTimeAgo } from '@/lib/dashboard-utils.jsx';

export const ChatAnalyticsPanel = ({ chatAnalytics }) => (
  <div className="mb-8" data-testid="chat-analytics-section">
    <h2 className="text-2xl font-semibold text-[#00d1ff] mb-6 border-b border-[#00d1ff]/30 pb-3">ZECHO CHAT ANALYTICS</h2>
    {!chatAnalytics.hasData ? (
      <div className="chart-container p-12 rounded-lg text-center">
        <Activity className="w-16 h-16 text-gray-600 mx-auto mb-4 opacity-30" />
        <p className="text-xl text-gray-400 mb-2">No chat data yet</p>
        <p className="text-sm text-gray-500 mb-4">Zecho conversations will appear here once users start chatting</p>
        <div className="inline-block bg-black/50 border border-gray-700 rounded-lg px-4 py-2 text-xs text-gray-400 font-mono">
          Supabase SQL Editor &rarr; paste zecho_chat_setup.sql &rarr; Run
        </div>
      </div>
    ) : (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiCard icon={<Activity className="w-6 h-6 text-[#00d1ff]" />} label="Total Conversations" value={chatAnalytics.kpis.totalConversations} color="text-[#00d1ff]" testId="chat-total-conversations" />
          <KpiCard icon={<FileText className="w-6 h-6 text-[#00ff41]" />} label="Total Messages" value={chatAnalytics.kpis.totalMessages} color="text-[#00ff41]" testId="chat-total-messages" />
          <KpiCard icon={<TrendingUp className="w-6 h-6 text-[#00d1ff]" />} label="Avg Messages/Conv" value={chatAnalytics.kpis.avgMessagesPerConv} color="text-[#00d1ff]" testId="chat-avg-messages" />
          <KpiCard icon={<Users className="w-6 h-6 text-[#00ff41]" />} label="Active Teams" value={chatAnalytics.kpis.activeTeams} color="text-[#00ff41]" testId="chat-active-teams" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="chart-container p-6 rounded-lg" data-testid="chat-volume-chart">
            <h3 className="text-xl font-semibold text-[#00d1ff] mb-4">CHAT VOLUME BY TEAM</h3>
            <div className="space-y-3">
              {chatAnalytics.teamVolume.slice(0, 10).map((team, index) => (
                <div key={team.team_slug} className="flex items-center gap-3">
                  <span className="text-sm text-gray-400 w-4">{index + 1}</span>
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: team.color, border: team.color === '#FFFFFF' || team.color === '#000000' ? '1px solid #666' : 'none' }} />
                  <span className="text-sm text-white flex-1 truncate">{team.team_name}</span>
                  <div className="flex-1 bg-gray-800 rounded-full h-2 relative overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(team.conversations / chatAnalytics.teamVolume[0]?.conversations) * 100}%`, backgroundColor: team.color }} />
                  </div>
                  <span className="text-sm font-bold text-white w-12 text-right">{team.conversations}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="chart-container p-6 rounded-lg" data-testid="chat-activity-timeline">
            <h3 className="text-xl font-semibold text-[#00d1ff] mb-4">CHAT ACTIVITY (30 DAYS)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chatAnalytics.activityTimeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
                <XAxis dataKey="day" stroke="#666" tick={{ fill: '#999', fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                <YAxis stroke="#666" tick={{ fill: '#999' }} />
                <Tooltip contentStyle={{ backgroundColor: '#000', border: '1px solid #333' }} labelStyle={{ color: '#00d1ff' }} />
                <Legend wrapperStyle={{ color: '#999' }} />
                <Line type="monotone" dataKey="conversations" stroke="#00d1ff" strokeWidth={2} dot={{ fill: '#00d1ff', r: 3 }} name="Conversations" />
                <Line type="monotone" dataKey="unique_users" stroke="#00ff41" strokeWidth={2} dot={{ fill: '#00ff41', r: 3 }} name="Unique Users" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="chart-container p-6 rounded-lg" data-testid="team-engagement-table">
          <h3 className="text-xl font-semibold text-[#00d1ff] mb-4">TEAM ENGAGEMENT</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-4 text-xs text-gray-400 uppercase tracking-wide">Team</th>
                  <th className="text-center py-3 px-4 text-xs text-gray-400 uppercase tracking-wide">Conversations</th>
                  <th className="text-center py-3 px-4 text-xs text-gray-400 uppercase tracking-wide">Messages</th>
                  <th className="text-center py-3 px-4 text-xs text-gray-400 uppercase tracking-wide">Avg Length</th>
                  <th className="text-center py-3 px-4 text-xs text-gray-400 uppercase tracking-wide">Most Active Day</th>
                  <th className="text-right py-3 px-4 text-xs text-gray-400 uppercase tracking-wide">Last Chat</th>
                </tr>
              </thead>
              <tbody>
                {chatAnalytics.teamEngagement.map((team) => (
                  <tr key={team.team_slug} className="border-b border-gray-800 hover:bg-white/5 transition-colors">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: team.color, border: team.color === '#FFFFFF' || team.color === '#000000' ? '1px solid #666' : 'none' }} />
                        <span className="text-white font-medium">{team.team_name}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center text-[#00d1ff] font-semibold">{team.conversations}</td>
                    <td className="py-4 px-4 text-center text-white">{team.messages}</td>
                    <td className="py-4 px-4 text-center text-gray-400">{team.avg_length} chars</td>
                    <td className="py-4 px-4 text-center text-white">{team.most_active_day}</td>
                    <td className="py-4 px-4 text-right text-gray-400">{formatTimeAgo(team.last_chat)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    )}
  </div>
);

const KpiCard = ({ icon, label, value, color, testId }) => (
  <div className="kpi-card p-4 rounded-lg" data-testid={testId}>
    <div className="flex items-center gap-2 mb-2">
      {icon}
      <span className="text-xs text-gray-400 uppercase tracking-wide">{label}</span>
    </div>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
  </div>
);
